import { SlidersHorizontal } from 'lucide-react';
import type { ColumnDef, VisibilityState, RowData } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { DataTableColumnVisibilityStore } from './types.js';

interface DataTableColumnToggleProps<TData extends RowData> {
  columns: ColumnDef<TData>[];
  userOverrides: DataTableColumnVisibilityStore;
  columnVisibility: VisibilityState;
  onToggle: (columnId: string, visible: boolean) => void;
  onReset: () => void;
}

function toTitleCase(s: string) {
  return s.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function DataTableColumnToggle<TData extends RowData>({
  columns,
  userOverrides,
  columnVisibility,
  onToggle,
  onReset,
}: DataTableColumnToggleProps<TData>) {
  const hasOverrides = Object.keys(userOverrides).length > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
          <SlidersHorizontal className="size-3.5" />
          Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-40">
        {columns.map((col) => {
          const id = (col as { id?: string }).id ?? (col as { accessorKey?: string }).accessorKey;
          if (!id) return null;
          const isMandatory = col.meta?.isMandatory;
          const label = col.meta?.label ?? toTitleCase(id);
          const checked = columnVisibility[id] !== false;

          if (isMandatory) {
            return (
              <DropdownMenuCheckboxItem key={id} checked disabled className="text-muted-foreground">
                {label}
              </DropdownMenuCheckboxItem>
            );
          }
          return (
            <DropdownMenuCheckboxItem
              key={id}
              checked={checked}
              onCheckedChange={(v) => onToggle(id, v)}
            >
              {label}
            </DropdownMenuCheckboxItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled={!hasOverrides} onClick={onReset} className="text-xs">
          Reset to defaults
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
